import {$wrapSelectionInMarkNode, MarkNode, SerializedMarkNode} from '@lexical/mark';
import {$applyNodeReplacement, NodeKey, RangeSelection, Spread} from 'lexical';
import {$getRoot, $isElementNode, LexicalNode} from 'lexical';

import styles from './AISuggestions.module.css';
import {AISuggestion} from './types';

const isWithinDisabledAISuggestionsScope = (element: HTMLElement) =>
  Boolean(element.closest(`.${styles['disabled-suggestions']}`));

// Custom tag to identify AI suggestion acceptance/rejection operations
export const AI_SUGGESTION_ACCEPTANCE_TAG = 'ai-suggestion-acceptance';

export type SerializedAISuggestionMarkNode = Spread<
  {
    suggestionData?: Partial<AISuggestion>;
  },
  SerializedMarkNode
>;

export class AISuggestionMarkNode extends MarkNode {
  __suggestionData: Partial<AISuggestion>;

  static getType(): string {
    return 'ai-suggestion-mark';
  }

  static getVersion(): number {
    return 1;
  }

  static clone(node: AISuggestionMarkNode): AISuggestionMarkNode {
    return new AISuggestionMarkNode(node.__suggestionData, node.__key);
  }

  static importDOM(): null {
    return null;
  }

  static getById(suggestionId: string): AISuggestionMarkNode[] {
    // Find all suggestion mark nodes with the given suggestionId
    // A single suggestion can be spread across multiple nodes
    const matchingSuggestionMarks: AISuggestionMarkNode[] = [];
    const traverse = (node: LexicalNode) => {
      if (node.getType() === 'ai-suggestion-mark') {
        const markNode = node as AISuggestionMarkNode;
        const markSuggestionId = markNode.getSuggestionId();
        if (markSuggestionId === suggestionId) {
          matchingSuggestionMarks.push(markNode);
        }
      }
      if ($isElementNode(node)) {
        node.getChildren().forEach(traverse);
      }
    };

    traverse($getRoot());

    return matchingSuggestionMarks;
  }

  constructor(suggestionData: Partial<AISuggestion>, key?: NodeKey) {
    // Handle case where suggestionData is undefined or null
    const safeSuggestionData = suggestionData || {};

    // Validate and provide defaults for required fields
    const validatedData: Partial<AISuggestion> = {
      ruleId: safeSuggestionData.ruleId || 'unknown',
      suggestedText: safeSuggestionData.suggestedText || '',
      confidence: safeSuggestionData.confidence || 0.8,
      suggestionId: safeSuggestionData.suggestionId || crypto.randomUUID(),
      headerId: safeSuggestionData.headerId,
      isRejected: safeSuggestionData.isRejected || false,
    };

    super([`suggestion-${validatedData.ruleId}-${validatedData.suggestionId}`], key);
    this.__suggestionData = validatedData;
  }

  createDOM(): HTMLElement {
    const mark = document.createElement('mark');
    mark.className = styles['suggestion-mark'];
    if (['spelling', 'grammar'].includes(this.__suggestionData.ruleId ?? '')) {
      mark.className += ` ${styles['spelling']}`;
    }

    // Add rejected class for fainter styling
    if (this.__suggestionData.isRejected) {
      mark.className += ` ${styles['rejected']}`;
    }

    mark.setAttribute('data-rule-id', this.__suggestionData.ruleId || 'unknown');
    mark.setAttribute('data-suggested-text', this.__suggestionData.suggestedText || '');
    mark.setAttribute('data-confidence', (this.__suggestionData.confidence || 0.8).toString());
    mark.setAttribute('data-suggestion-id', this.getSuggestionId());
    mark.setAttribute('data-is-rejected', (this.__suggestionData.isRejected || false).toString());

    // Add header ID if available
    if (this.__suggestionData.headerId) {
      mark.setAttribute('data-header-id', this.__suggestionData.headerId);
    }

    // Attach event listeners for hover effect
    mark.addEventListener('mouseenter', (event) => this.handleMouseEnter(event));
    mark.addEventListener('mouseleave', (event) => this.handleMouseLeave(event));

    return mark;
  }

  handleMouseEnter(event: MouseEvent) {
    const suggestionId = this.getSuggestionId();
    if (!suggestionId) return;

    // Check if this suggestion should defer to child suggestions
    const hoveredElement = event.target as HTMLElement;
    if (isWithinDisabledAISuggestionsScope(hoveredElement)) {
      return;
    }
    if (this.shouldDeferToOtherSuggestionDOM(hoveredElement, suggestionId)) {
      return;
    }

    // Skip rejected suggestions
    if (this.__suggestionData.isRejected) return;

    // Stop event propagation to prevent parent suggestions from handling this event
    event.stopPropagation();
    event.preventDefault();

    // Add hover to all elements with the same suggestion ID
    document.querySelectorAll(`[data-suggestion-id="${suggestionId}"]`).forEach((el) => {
      el.classList.add(styles.highlighted);
    });
    return false;
  }

  handleMouseLeave(event: MouseEvent) {
    const suggestionId = this.getSuggestionId();
    if (!suggestionId) return;

    // Check if this suggestion should defer to child suggestions (should match enter logic)
    const hoveredElement = event.target as HTMLElement;
    if (isWithinDisabledAISuggestionsScope(hoveredElement)) {
      document.querySelectorAll(`[data-suggestion-id="${suggestionId}"]`).forEach((el) => {
        el.classList.remove(styles.highlighted);
      });
      return;
    }
    if (this.shouldDeferToOtherSuggestionDOM(hoveredElement, suggestionId)) {
      return;
    }

    // Stop event propagation to prevent parent suggestions from handling this event
    event.stopPropagation();

    // Remove hover classes from all elements with the same suggestion ID
    document.querySelectorAll(`[data-suggestion-id="${suggestionId}"]`).forEach((el) => {
      el.classList.remove(styles.highlighted);
    });
  }

  updateDOM(prevNode: AISuggestionMarkNode): boolean {
    const prevData = prevNode.__suggestionData;
    const currentData = this.__suggestionData;

    const hasChanges =
      (prevData.suggestedText || '') !== (currentData.suggestedText || '') ||
      (prevData.confidence || 0.8) !== (currentData.confidence || 0.8) ||
      (prevData.isRejected || false) !== (currentData.isRejected || false);

    return hasChanges;
  }

  /**
   * Check if this suggestion contains child suggestions that should take precedence.
   * Returns true if this suggestion should defer to a child suggestion.
   */
  shouldDeferToOtherSuggestionDOM(
    hoveredElement: HTMLElement,
    currentSuggestionId: string
  ): boolean {
    try {
      // Get current suggestion's text length from DOM elements
      const getCurrentTextLength = (suggestionId: string): number => {
        const elements = document.querySelectorAll(`[data-suggestion-id="${suggestionId}"]`);
        let totalText = '';
        elements.forEach((el) => {
          totalText += el.textContent || '';
        });
        return totalText.length;
      };

      const currentTextLength = getCurrentTextLength(currentSuggestionId);

      // Check if hovered element contains smaller suggestions (children)
      const childSuggestions = hoveredElement.querySelectorAll('[data-suggestion-id]');
      for (const childElement of childSuggestions) {
        const childSuggestionId = childElement.getAttribute('data-suggestion-id');
        if (childSuggestionId && childSuggestionId !== currentSuggestionId) {
          const childTextLength = getCurrentTextLength(childSuggestionId);
          if (childTextLength < currentTextLength) {
            return true; // Defer to smaller child suggestion
          }
        }
      }

      // Check if hovered element is contained within smaller suggestions (parents)
      let parentElement = hoveredElement.parentElement;
      while (parentElement && parentElement !== document.body) {
        if (parentElement.hasAttribute('data-suggestion-id')) {
          const parentSuggestionId = parentElement.getAttribute('data-suggestion-id');
          if (parentSuggestionId && parentSuggestionId !== currentSuggestionId) {
            const parentTextLength = getCurrentTextLength(parentSuggestionId);
            if (parentTextLength < currentTextLength) {
              return true; // Defer to smaller parent suggestion
            }
          }
        }
        parentElement = parentElement.parentElement;
      }

      return false;
    } catch (error) {
      console.warn('Error checking for child suggestions in DOM:', error);
      return false;
    }
  }

  /**
   * Check if the hovered DOM element should defer to other suggestions.
   * Lexical version that accesses Lexical nodes - must be called within editor.read().
   */
  /*private shouldDeferToOtherSuggestion(hoveredElement: HTMLElement, currentSuggestionId: string): boolean {
    try {
      // Get current suggestion's original text length
      const currentNodes = AISuggestionMarkNode.getById(currentSuggestionId);
      const currentOriginalText = currentNodes.map(node => node.getTextContent()).join('');
      const currentTextLength = currentOriginalText.length;

      // Helper to get original text length for a suggestion ID
      const getOriginalTextLength = (suggestionId: string): number => {
        const nodes = AISuggestionMarkNode.getById(suggestionId);
        return nodes.map(node => node.getTextContent()).join('').length;
      };

      // Check if hovered element contains smaller suggestions (children)
      const childSuggestions = hoveredElement.querySelectorAll('[data-suggestion-id]');
      for (const childElement of childSuggestions) {
        const childSuggestionId = childElement.getAttribute('data-suggestion-id');
        if (childSuggestionId && childSuggestionId !== currentSuggestionId) {
          const childTextLength = getOriginalTextLength(childSuggestionId);
          if (childTextLength < currentTextLength) {
            return true; // Defer to smaller child suggestion
          }
        }
      }

      // Check if hovered element is contained within smaller suggestions (parents)
      let parentElement = hoveredElement.parentElement;
      while (parentElement && parentElement !== document.body) {
        if (parentElement.hasAttribute('data-suggestion-id')) {
          const parentSuggestionId = parentElement.getAttribute('data-suggestion-id');
          if (parentSuggestionId && parentSuggestionId !== currentSuggestionId) {
            const parentTextLength = getOriginalTextLength(parentSuggestionId);
            if (parentTextLength < currentTextLength) {
              return true; // Defer to smaller parent suggestion
            }
          }
        }
        parentElement = parentElement.parentElement;
      }

      // Also check if the hovered element itself is a different suggestion that's smaller
      const hoveredSuggestionId = hoveredElement.getAttribute('data-suggestion-id');
      if (hoveredSuggestionId && hoveredSuggestionId !== currentSuggestionId) {
        const hoveredTextLength = getOriginalTextLength(hoveredSuggestionId);
        if (hoveredTextLength < currentTextLength) {
          return true; // Defer to smaller hovered suggestion
        }
      }

      // Additional check: Look for any overlapping suggestions in the entire document
      // since DOM hierarchy might not match Lexical hierarchy
      const allSuggestionElements = document.querySelectorAll('[data-suggestion-id]');
      for (const element of allSuggestionElements) {
        const elementSuggestionId = element.getAttribute('data-suggestion-id');
        if (elementSuggestionId && elementSuggestionId !== currentSuggestionId) {
          // Check if this element overlaps with our hovered element
          const elementRect = element.getBoundingClientRect();
          const hoveredRect = hoveredElement.getBoundingClientRect();

          const overlaps = !(
            elementRect.right < hoveredRect.left ||
            elementRect.left > hoveredRect.right ||
            elementRect.bottom < hoveredRect.top ||
            elementRect.top > hoveredRect.bottom
          );

          if (overlaps) {
            const elementTextLength = getOriginalTextLength(elementSuggestionId);
            if (elementTextLength < currentTextLength) {
              return true; // Defer to smaller overlapping suggestion
            }
          }
        }
      }

      return false;
    } catch (error) {
      console.warn('Error checking for overlapping suggestions in DOM:', error);
      return false;
    }
  }*/

  /**
   * Check if this suggestion overlaps with any smaller AI suggestions in the parent/child tree.
   * Compares the original text length (across all nodes with same suggestion ID) rather than individual node content.
   * Used to implement precedence where shorter suggestions take priority.
   */
  overlapsWithSmallerSuggestion(): boolean {
    try {
      // Get the original text for this suggestion by combining all nodes with the same suggestion ID
      const currentSuggestionId = this.getSuggestionId();
      const currentSuggestionNodes = AISuggestionMarkNode.getById(currentSuggestionId);
      const currentOriginalText = currentSuggestionNodes
        .map((node) => node.getTextContent())
        .join('');
      const currentTextLength = currentOriginalText.length;

      // Helper to get the original text length for a suggestion ID
      const getOriginalTextLength = (suggestionId: string): number => {
        const nodes = AISuggestionMarkNode.getById(suggestionId);
        return nodes.map((node) => node.getTextContent()).join('').length;
      };

      // Helper function to recursively find all AISuggestionMarkNodes in a subtree
      const findAllSuggestionNodes = (node: LexicalNode): AISuggestionMarkNode[] => {
        const suggestions: AISuggestionMarkNode[] = [];

        if (node !== this && $isAISuggestionMarkNode(node)) {
          suggestions.push(node);
        }

        if ($isElementNode(node)) {
          node.getChildren().forEach((child) => {
            suggestions.push(...findAllSuggestionNodes(child));
          });
        }

        return suggestions;
      };

      // Track unique suggestion IDs we've already checked
      const checkedSuggestionIds = new Set<string>([currentSuggestionId]);

      // Check children for smaller suggestions
      const childSuggestions = this.getChildren().flatMap(findAllSuggestionNodes);
      for (const suggestion of childSuggestions) {
        const suggestionId = suggestion.getSuggestionId();
        if (checkedSuggestionIds.has(suggestionId)) continue;
        checkedSuggestionIds.add(suggestionId);

        const suggestionTextLength = getOriginalTextLength(suggestionId);
        if (suggestionTextLength < currentTextLength) {
          return true; // Found a smaller suggestion in children
        }
      }

      // Check parents and their subtrees for smaller suggestions
      let currentParent = this.getParent();
      while (currentParent) {
        if ($isAISuggestionMarkNode(currentParent)) {
          const parentSuggestionId = currentParent.getSuggestionId();
          if (!checkedSuggestionIds.has(parentSuggestionId)) {
            checkedSuggestionIds.add(parentSuggestionId);

            const parentTextLength = getOriginalTextLength(parentSuggestionId);
            if (parentTextLength < currentTextLength) {
              return true; // Found a smaller suggestion in parents
            }
          }
        }

        // Also check siblings of ancestors for smaller suggestions
        if ($isElementNode(currentParent)) {
          const siblingSuggestions = currentParent.getChildren().flatMap(findAllSuggestionNodes);
          for (const suggestion of siblingSuggestions) {
            const suggestionId = suggestion.getSuggestionId();
            if (checkedSuggestionIds.has(suggestionId)) continue;
            checkedSuggestionIds.add(suggestionId);

            const suggestionTextLength = getOriginalTextLength(suggestionId);
            if (suggestionTextLength < currentTextLength) {
              return true; // Found a smaller suggestion in parent's subtree
            }
          }
        }

        currentParent = currentParent.getParent();
      }

      return false;
    } catch (error) {
      console.warn('Error checking for overlapping smaller suggestions:', error);
      return false;
    }
  }

  getSuggestionData(): Partial<AISuggestion> {
    return {
      ruleId: this.__suggestionData.ruleId || 'unknown',
      suggestedText: this.__suggestionData.suggestedText || '',
      confidence: this.__suggestionData.confidence || 0.8,
      suggestionId: this.__suggestionData.suggestionId || crypto.randomUUID(),
      headerId: this.__suggestionData.headerId,
      isRejected: this.__suggestionData.isRejected || false,
    };
  }

  getSuggestionId(): string {
    return this.__suggestionData.suggestionId || crypto.randomUUID();
  }

  setRejected(isRejected: boolean): this {
    const self = this.getWritable();
    self.__suggestionData.isRejected = isRejected;
    return self;
  }

  isRejected(): boolean {
    return this.__suggestionData.isRejected || false;
  }

  static importJSON(serializedNode: SerializedAISuggestionMarkNode): AISuggestionMarkNode {
    // Handle case where serializedNode or suggestionData might be undefined
    const suggestionData = serializedNode?.suggestionData || {};
    return new AISuggestionMarkNode(suggestionData);
  }

  exportJSON(): SerializedAISuggestionMarkNode {
    return {
      ...super.exportJSON(),
      type: 'ai-suggestion-mark',
      version: 1,
      suggestionData: {
        ruleId: this.__suggestionData.ruleId || 'unknown',
        suggestedText: this.__suggestionData.suggestedText || '',
        confidence: this.__suggestionData.confidence || 0.8,
        suggestionId: this.__suggestionData.suggestionId || crypto.randomUUID(),
        headerId: this.__suggestionData.headerId,
        isRejected: this.__suggestionData.isRejected || false,
      },
    };
  }
}

export function $createAISuggestionMarkNode(
  suggestionData: Partial<AISuggestion>
): AISuggestionMarkNode {
  return $applyNodeReplacement(new AISuggestionMarkNode(suggestionData));
}

export function $wrapSelectionInAISuggestionMarkNode(
  selection: RangeSelection,
  suggestionData: Partial<AISuggestion>
) {
  const ruleId = suggestionData.ruleId || 'unknown';
  const suggestionId = suggestionData.suggestionId || crypto.randomUUID();

  $wrapSelectionInMarkNode(selection, false, `suggestion-${ruleId}-${suggestionId}`, () =>
    $createAISuggestionMarkNode(suggestionData)
  );
}

export function $isAISuggestionMarkNode(node: any): node is AISuggestionMarkNode {
  return node instanceof AISuggestionMarkNode;
}
